export type {
  ThreeDSProvider,
  BaseThreeDSResult as ThreeDSResult,
  BaseCheckoutData
} from './index';

import type { BaseThreeDSResult } from './index';
import { getScriptUrl } from './config';

export {
  getThreeDSToken,
  getThreeDSTokenWithRetry,
  determineThreeDSProvider,
  validateThreeDSData,
  clearTokenCache,
  getProviderConfig
} from './index';

export const validateFieldsFor3DS = (data: any): void => {
  const requiredFields = ['name', 'cardNumber', 'cardExpiration', 'cardCvv'];
  const missingFields = requiredFields.filter(field =>
    !data[field] || data[field].toString().trim() === ''
  );

  if (missingFields.length > 0) {
    throw new Error(`Campos obrigatórios para 3DS: ${missingFields.join(', ')}`);
  }

  const [expMonth, expYear] = (data.cardExpiration || '').split('/');
  if (!expMonth || !expYear || expMonth.length !== 2 || expYear.length !== 2) {
    throw new Error('Data de expiração inválida para 3DS');
  }

  const cardNumber = data.cardNumber.replace(/\D/g, '');
  if (cardNumber.length < 13) {
    throw new Error('Número do cartão inválido para 3DS');
  }

  if (data.cardCvv.length < 3) {
    throw new Error('CVV inválido para 3DS');
  }
};

export const initializeCielo3DS = async (
  accessToken: string,
  orderData: {
    amount: number;
    orderNumber: string;
    currency: string;
    paymentMethod: 'credit' | 'debit';
    cardNumber: string;
    cardExpirationMonth: string;
    cardExpirationYear: string;
  }
): Promise<BaseThreeDSResult> => {
  if (!accessToken || typeof accessToken !== 'string') {
    throw new Error('Token 3DS inválido ou ausente');
  }
  if (!orderData) {
    throw new Error('Dados do pedido ausentes para Cielo 3DS');
  }

  return new Promise((resolve, reject) => {
    try {
      const container = document.createElement('div');
      container.style.display = 'none';

      const createHidden = (cls: string, value: string) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.className = cls;
        input.value = value;
        container.appendChild(input);
      };

      createHidden('bpmpi_auth', 'true');
      createHidden('bpmpi_accesstoken', accessToken);
      createHidden('bpmpi_ordernumber', orderData.orderNumber);
      createHidden('bpmpi_currency', orderData.currency);
      createHidden('bpmpi_totalamount', String(orderData.amount));
      createHidden('bpmpi_paymentmethod', orderData.paymentMethod);
      createHidden('bpmpi_cardnumber', orderData.cardNumber);
      createHidden('bpmpi_cardexpirationmonth', orderData.cardExpirationMonth.padStart(2, '0'));
      createHidden('bpmpi_cardexpirationyear', orderData.cardExpirationYear);

      document.body.appendChild(container);

      (window as any).bpmpi_config = () => ({
        Environment: (import.meta.env.VITE_CIELO_3DS_SCRIPT || '').includes('sandbox') ? 'SDB' : 'PRD',
        Debug: true,
        onSuccess: (e: any) => {
          resolve({
            success: true,
            provider: 'cielo',
            cavv: e.Cavv,
            xid: e.Xid,
            eci: e.Eci,
            version: e.Version,
            referenceId: e.ReferenceId
          });
        },
        onFailure: (e: any) => {
          resolve({
            success: false,
            provider: 'cielo',
            cavv: null,
            xid: e.Xid,
            eci: e.Eci,
            version: e.Version,
            referenceId: e.ReferenceId
          });
        },
        onUnenrolled: (e: any) => {
          resolve({
            success: false,
            provider: 'cielo',
            cavv: null,
            xid: e.Xid,
            eci: e.Eci,
            version: e.Version,
            referenceId: e.ReferenceId
          });
        },
        onDisabled: () => resolve({ success: false, provider: 'cielo' }),
        onError: () => resolve({ success: false, provider: 'cielo' }),
        onUnsupportedBrand: () => resolve({ success: false, provider: 'cielo' })
      });

      const cieloScriptUrl = import.meta.env.VITE_CIELO_3DS_SCRIPT;
      if (!cieloScriptUrl) {
        reject(new Error('VITE_CIELO_3DS_SCRIPT não configurado'));
        return;
      }

      const existing = document.querySelector(`script[src="${cieloScriptUrl}"]`);
      const runAuth = () => {
        if (typeof (window as any).bpmpi_authenticate === 'function') {
          (window as any).bpmpi_authenticate();
        } else {
          reject(new Error('SDK Cielo não carregada'));
        }
      };

      const environment = cieloScriptUrl.includes('sandbox') ? 'SDB' : 'PRD';
      
      const loadCardinalScript = () => {
        const cardinalUrl = environment === 'SDB' 
          ? 'https://songbirdstag.cardinalcommerce.com/edge/v1/songbird.js'
          : 'https://songbird.cardinalcommerce.com/edge/v1/songbird.js';
        
        const existingCardinal = document.querySelector(`script[src="${cardinalUrl}"]`);
        
        if (existingCardinal) {
          loadCieloScript();
        } else {
          const cardinalScript = document.createElement('script');
          cardinalScript.src = cardinalUrl;
          cardinalScript.async = true;
          cardinalScript.onload = () => loadCieloScript();
          cardinalScript.onerror = () => {
            console.error('Falha ao carregar Cardinal Commerce SDK');
            loadCieloScript();
          };
          document.head.appendChild(cardinalScript);
        }
      };

      const loadCieloScript = () => {
        if (existing) {
          setTimeout(runAuth, 100);
        } else {
          const script = document.createElement('script');
          script.src = cieloScriptUrl;
          script.async = true;
          script.onload = () => setTimeout(runAuth, 100);
          script.onerror = () => reject(new Error('Falha ao carregar SDK Cielo'));
          document.head.appendChild(script);
        }
      };

      loadCardinalScript();
    } catch (err) {
      reject(err);
    }
  });
};

export const initializePagarme3DS = async (
  _token: string,
  _tdsData: any
): Promise<BaseThreeDSResult> => {
  console.warn('initializePagarme3DS is deprecated. Use the new modular structure with PagarmeThreeDSecure component.');

  return new Promise((resolve) => {
    resolve({
      success: false,
      provider: 'pagarme' as const,
      error: 'Function deprecated - use new modular structure'
    });
  });
};

export const initialize3DS = async (
  provider: 'cielo' | 'pagarme',
  token: string,
  data: any,
  options?: any
): Promise<BaseThreeDSResult> => {
  if (!token || typeof token !== 'string') {
    throw new Error('Token 3DS inválido ou ausente');
  }

  if (provider === 'cielo') {
    return initializeCielo3DS(token, options?.orderData);
  } else if (provider === 'pagarme') {
    return new Promise((resolve, reject) => {
      try {
        if (!data || !options?.cardData) {
          reject(new Error('Dados obrigatórios ausentes para 3DS Pagar.me'));
          return;
        }

        let form = document.querySelector('form[data-pagarmecheckout-form]') as HTMLFormElement;
        if (!form) {
          form = document.createElement('form');
          form.setAttribute('data-pagarmecheckout-form', '');
          form.style.display = 'none';
          document.body.appendChild(form);
        }

        const ensureField = (elementName: string, value: string, type: 'input' | 'span' = 'input') => {
          let field = form.querySelector(`[data-pagarmecheckout-element="${elementName}"]`);
          if (!field) {
            if (type === 'span') {
              field = document.createElement('span');
            } else {
              field = document.createElement('input');
              (field as HTMLInputElement).type = 'hidden';
            }
            field.setAttribute('data-pagarmecheckout-element', elementName);
            form.appendChild(field);
          }

          if (field instanceof HTMLInputElement) {
            field.value = value;
          } else if (field instanceof HTMLSpanElement) {
            field.textContent = value;
          }
        };

        const cardData = options.cardData;

        ensureField('holder_name', cardData.holderName);
        ensureField('number', cardData.cardNumber.replace(/\D/g, ''));
        ensureField('cvv', cardData.cvv);
        ensureField('exp_date', `20${cardData.expYear}-${cardData.expMonth.padStart(2, '0')}`);
        ensureField('brand', '', 'span');

        let iframe = form.querySelector('#challengeIframe') as HTMLIFrameElement;
        if (!iframe) {
          iframe = document.createElement('iframe');
          iframe.id = 'challengeIframe';
          iframe.name = 'challengeIframe';
          iframe.setAttribute('data-pagarmecheckout-element', 'challengeIframe');
          iframe.style.display = 'none';
          iframe.style.width = '100%';
          iframe.style.height = '400px';
          form.appendChild(iframe);
        }

                const showChallengeModal = () => {
          const iframe = document.getElementById('challengeIframe') as HTMLIFrameElement;
          const overlay = document.getElementById('challengeOverlay') as HTMLDivElement;
          if (iframe && overlay) {
            const isMobile = window.innerWidth <= 768;

            if (isMobile) {
              iframe.style.width = '95vw';
              iframe.style.height = '85vh';
              iframe.style.maxWidth = '95vw';
              iframe.style.maxHeight = '85vh';
              iframe.style.minWidth = '300px';
              iframe.style.minHeight = '400px';
              iframe.style.borderRadius = '12px';
              iframe.style.top = '50%';
              iframe.style.left = '50%';
              iframe.style.transform = 'translate(-50%, -50%)';
            } else {
              iframe.style.width = 'min(90vw, 500px)';
              iframe.style.height = 'min(80vh, 600px)';
              iframe.style.maxWidth = '500px';
              iframe.style.maxHeight = '600px';
              iframe.style.minWidth = '320px';
              iframe.style.minHeight = '400px';
              iframe.style.borderRadius = '8px';
            }

            iframe.style.display = 'block';
            overlay.style.display = 'block';
            document.body.style.overflow = 'hidden';

            iframe.style.transition = 'all 0.3s ease-in-out';
            overlay.style.transition = 'opacity 0.3s ease-in-out';
          }
        };

        const hideChallengeModal = () => {
          const iframe = document.getElementById('challengeIframe') as HTMLIFrameElement;
          const overlay = document.getElementById('challengeOverlay') as HTMLDivElement;
          if (iframe && overlay) {
            iframe.style.display = 'none';
            overlay.style.display = 'none';
            document.body.style.overflow = 'auto'; // Restaura scroll da página
          }
        };

        const observer = new MutationObserver(() => {
          const iframe = document.getElementById('challengeIframe') as HTMLIFrameElement;
          if (iframe && iframe.contentDocument && iframe.contentDocument.body.innerHTML.trim() !== '') {
            showChallengeModal();
          }
        });

        if (iframe) {
          observer.observe(iframe, { childList: true, subtree: true });
        }

                const handleResize = () => {
          const iframe = document.getElementById('challengeIframe') as HTMLIFrameElement;
          if (iframe && iframe.style.display === 'block') {
            const isMobile = window.innerWidth <= 768;

            if (isMobile) {
              iframe.style.width = '95vw';
              iframe.style.height = '85vh';
              iframe.style.maxWidth = '95vw';
              iframe.style.maxHeight = '85vh';
            } else {
              iframe.style.width = 'min(90vw, 500px)';
              iframe.style.height = 'min(80vh, 600px)';
              iframe.style.maxWidth = '500px';
              iframe.style.maxHeight = '600px';
            }
          }
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);

        let attempts = 0;
        const handleResult = async (result: any) => {
          if (result?.error) {
            if (attempts < 1) {
              try {
                attempts += 1;
                const newToken = await (await import('./index')).getThreeDSTokenWithRetry('pagarme', 1);
                if (window.Script3ds?.init3ds) {
              window.Script3ds.init3ds(newToken, data, (r: any) => { void handleResult(r); }, '03');
                  return;
                }
              } catch (e) {
                // fallthrough to reject below
              }
            }
            const details = typeof result === 'object' ? JSON.stringify(result) : String(result);
            reject(new Error(details));
            return;
          }

                              hideChallengeModal();

          window.removeEventListener('resize', handleResize);
          window.removeEventListener('orientationchange', handleResize);
          observer.disconnect();

          resolve({
            success: result.trans_status === 'Y' || result.trans_status === 'A',
            provider: 'pagarme' as const,
            trans_status: result.trans_status,
            tds_server_trans_id: result.tds_server_trans_id,
            eci: result.trans_status || '05'
          });
        };

        const existingScript = document.getElementById('pagarme-3ds-script');
        if (existingScript) existingScript.remove();

        const script = document.createElement('script');
        script.id = 'pagarme-3ds-script';
        const pagarme3dsScriptUrl = getScriptUrl('pagarme');
        script.src = pagarme3dsScriptUrl;

        script.onload = () => {
          setTimeout(() => {
            if (window.Script3ds?.init3ds) {
              window.Script3ds.init3ds(token, data, (r: any) => { void handleResult(r); }, '03');
            } else {
              reject(new Error('SDK Pagar.me não carregado'));
            }
          }, 200);
        };

        script.onerror = () => {
          reject(new Error('Falha ao carregar SDK Pagar.me'));
        };

        document.head.appendChild(script);
      } catch (error) {
        reject(error);
      }
    });
  } else {
    throw new Error(`Unsupported 3DS provider: ${provider}`);
  }
};


